import React, { useCallback, useMemo, useRef, useState } from 'react';
import { LinearGradient } from 'expo-linear-gradient';
import { Redirect, useRouter } from 'expo-router';
import { defineMessages, FormattedMessage } from 'react-intl';
import { ScrollView, StyleSheet, View } from 'react-native';

import { DisplayText, Icon, theme, useResponsive, useIsFocused, Container } from '@sg/garnish';
import type { DeliveryOrderDetailInput } from '@sg/graphql-schema';
import {
  ADDED_TO_BAG_CHECK_SIZE,
  ANIMATION_DELAYS,
  getAddedLineItem,
  Upsell,
  useDynamicBagButtonLabel,
  useUpsellContent,
} from '@sg/shared-fe/features/menu';

import { useContentfulClient } from '@order/Contentful';
import { useTelemetry, useTrackEffect } from '@order/Telemetry';

import { useShouldShowUpsellScreen, useUpsellsV2 } from '../../hooks';
import { useCart } from '../../hooks/useCart';
import { UpsellLineItemCard, UpsellProductOption } from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const UpsellScreen = () => {
  const { cart, cartOrderType } = useCart();

  // ─── Missing Data ────────────────────────────────────────────────────

  if (!cart?.restaurant?.id || !cart?.restaurant?.slug) {
    return <Redirect href="/locations" />;
  }

  const restaurantSlug = cart.restaurant.slug;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <UpsellScreenReady
      restaurantSlug={restaurantSlug}
      cart={cart}
      bagReferrer={cartOrderType === 'delivery' ? 'DeliveryMenu' : 'Menu'}
    />
  );
};

const UpsellScreenReady = (props: ReadyProps) => {
  const { cart, bagReferrer } = props;

  const router = useRouter();

  const { track } = useTelemetry();
  const { updateBagButtonLabel } = useDynamicBagButtonLabel();
  const shouldShowUpsellScreen = useShouldShowUpsellScreen();
  const { minWidth } = useResponsive();

  const isScreenFocused = useIsFocused();

  const contentfulClient = useContentfulClient();
  const { mappedContent } = useUpsellContent({
    contentfulClient,
    pause: !isScreenFocused,
  });

  // @DEV: We can't allow users to exit screen before upsell product stepper has resolved.
  const [openSteppers, setOpenSteppers] = useState<string[]>([]); // prettier-ignore
  const areSteppersShown = Boolean(openSteppers.length);

  const updateOpenSteppers = useCallback(
    (productId: string, isStepperShown: boolean) => {
      setOpenSteppers((openSteppers) =>
        isStepperShown
          ? [...openSteppers, productId]
          : openSteppers.filter((id) => id !== productId),
      );
    },
    [setOpenSteppers],
  );

  const deliveryOrderDetail = cart?.deliveryOrderDetail;
  const deliveryDetails = useMemo<DeliveryOrderDetailInput | undefined>(() => {
    const addressId = deliveryOrderDetail?.address?.id;

    if (!addressId) return;

    return {
      addressId,
      vendor: deliveryOrderDetail.vendor,
      vendorRestaurantId: deliveryOrderDetail.vendorRestaurantId,
      deliveryFee: deliveryOrderDetail.deliveryFee,
      tip: deliveryOrderDetail.tip,
    };
  }, [
    deliveryOrderDetail?.address?.id,
    deliveryOrderDetail?.vendor,
    deliveryOrderDetail?.vendorRestaurantId,
    deliveryOrderDetail?.deliveryFee,
    deliveryOrderDetail?.tip,
  ]);

  const { upsellProducts, productSize, isLoadingUpsells } = useUpsellsV2();

  const { current: maybeAddedLineItem } = useRef(
    getAddedLineItem({
      cartLineItems: cart?.lineItems ?? [],
      upsellProducts,
    }),
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useTrackEffect('upsell_modal.view');

  // ─── Navigation ─────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    track('upsell_modal.close');

    router.back();
  }, [track, router]);

  const handleContinue = useCallback(() => {
    router.replace({ pathname: '/bag', params: { referrer: bagReferrer } });
  }, [router, bagReferrer]);

  // ─── Render ──────────────────────────────────────────────────────────────

  if (isLoadingUpsells) {
    return null;
  }

  if (!shouldShowUpsellScreen) {
    return <Redirect href={{ pathname: '/bag', params: { referrer: bagReferrer } }} />;
  }

  return (
    <View style={styles.mainContainer}>
      <Upsell.Backdrop onPress={handleClose} />

      <Container wrapperStyle={styles.contentContainer} style={styles.contentContainer}>
        <Upsell.ScreenContainer style={styles.screenContainer}>
          <Upsell.Container style={styles.container}>
            {minWidth.isMD ? (
              <Upsell.AnimatedWrapper animationDelay={ANIMATION_DELAYS.ITEM_CONFIRMATION}>
                <Upsell.ConfirmationContainer style={styles.confirmationContainer}>
                  <Upsell.ConfirmationHeader>
                    <DisplayText sizeMatch={['32']}>
                      <FormattedMessage {...messages.addedToBag} />
                    </DisplayText>
                    <Icon
                      name="Progress4"
                      width={ADDED_TO_BAG_CHECK_SIZE}
                      height={ADDED_TO_BAG_CHECK_SIZE}
                      color={theme.colors.KALE}
                    />
                  </Upsell.ConfirmationHeader>

                  {maybeAddedLineItem ? (
                    <UpsellLineItemCard lineItem={maybeAddedLineItem as any} />
                  ) : null}
                </Upsell.ConfirmationContainer>
              </Upsell.AnimatedWrapper>
            ) : null}

            <Upsell.AddOnsContainer palette="cream" style={styles.addOnsContainer}>
              <Upsell.AnimatedWrapper
                animationDelay={ANIMATION_DELAYS.UPSELL_OPTIONS}
                style={styles.addOnsAnimatedWrapper}
              >
                <Upsell.AddOnsHeader size={productSize}>
                  <DisplayText sizeMatch={['32']}>{mappedContent.upsellCta}</DisplayText>
                  <Upsell.CloseIcon onPress={handleClose} disabled={areSteppersShown} />
                </Upsell.AddOnsHeader>

                <View style={styles.scrollContainer}>
                  {minWidth.isLG ? (
                    <Upsell.OptionsContainer size={productSize} itemsPerRow={2}>
                      {upsellProducts.map((product, index) => (
                        <UpsellProductOption
                          key={product.id}
                          product={product}
                          size={productSize}
                          withBorder={index < upsellProducts.length - 1}
                          onAddToBag={updateBagButtonLabel}
                          updateOpenSteppers={updateOpenSteppers}
                          position={index}
                          deliveryDetails={deliveryDetails}
                        />
                      ))}
                    </Upsell.OptionsContainer>
                  ) : (
                    <>
                      <ScrollView bounces={false} showsVerticalScrollIndicator>
                        <Upsell.OptionsContainer size={productSize} itemsPerRow={1}>
                          {upsellProducts.map((product, index) => (
                            <UpsellProductOption
                              key={product.id}
                              product={product}
                              size={productSize}
                              withBorder={index < upsellProducts.length - 1}
                              onAddToBag={updateBagButtonLabel}
                              updateOpenSteppers={updateOpenSteppers}
                              position={index}
                              deliveryDetails={deliveryDetails}
                            />
                          ))}
                        </Upsell.OptionsContainer>
                      </ScrollView>

                      <LinearGradient
                        style={styles.scrollGradient}
                        pointerEvents="none"
                        colors={SCROLL_GRADIENT_COLORS}
                      />
                    </>
                  )}
                </View>

                <Upsell.Footer style={styles.footer}>
                  <Upsell.ActionButton
                    variant="continue"
                    onPress={handleContinue}
                    disabled={areSteppersShown}
                    palette="primary"
                  />
                </Upsell.Footer>
              </Upsell.AnimatedWrapper>
            </Upsell.AddOnsContainer>
          </Upsell.Container>
        </Upsell.ScreenContainer>
      </Container>
    </View>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ReadyProps = {
  restaurantSlug: string;
  cart: NonNullable<ReturnType<typeof useCart>['cart']>;
  bagReferrer: 'Menu' | 'DeliveryMenu';
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addedToBag: {
    defaultMessage: 'Added to bag',
    description: 'Upsell Screen | Added to bag confirmation',
  },
});

// ─── Constants ────────────────────────────────────────────────────────────────

const SCROLL_GRADIENT_COLORS = [
  theme.colors.OPACITY.CREAM.TRANSPARENT,
  theme.colors.CREAM,
] as const;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  addOnsAnimatedWrapper: {
    flex: 1,
  },
  mainContainer: {
    flex: 1,
    paddingTop: theme.spacing['10'],
  },
  contentContainer: {
    flex: 1,
  },
  screenContainer: {
    justifyContent: 'center',
    pointerEvents: 'box-none',
  },
  container: {
    overflow: 'hidden',
    borderRadius: theme.radius.large,
    width: '100%',
    maxWidth: 1200,
    maxHeight: 840,
    alignSelf: 'center',
  },
  confirmationContainer: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.BLACK,
    height: '100%',
  },
  addOnsContainer: {
    borderRadius: theme.radius.large,
    paddingTop: theme.spacing['10'],
  },
  footer: {
    paddingTop: theme.spacing['6'],
  },
  scrollContainer: {
    flex: 1,
  },
  scrollGradient: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 40,
  },
});
